import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import useCartesiaTTS from "../hooks/useCartesiaTTS";
import useMediaPipeProctoring from "../hooks/useMediaPipeProctoring";
import api from "../lib/api";
import { computeDelivery, computeDeliveryScore, getMetricColor, COLOR_CLASSES } from "../lib/speechDelivery";
import { track } from "../lib/analytics";
import { LogoDark } from "../components/Logo";

const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

const PHASE = {
  permissions:     "permissions",
  ready:           "ready",
  speaking:        "speaking",
  recording:       "recording",
  evaluating:      "evaluating",
  processing:      "processing",
  delivery:        "delivery",
  complete:        "complete",
  error:           "error",
  expired:         "expired",
  already_done:    "already_done",
  camera_required: "camera_required",
  retake_window:   "retake_window",
};

const MODULE_ORDER = ["hr", "technical", "behavioral", "custom"];

// Timing
const MIN_ANSWER_SECS       = 8;    // minimum before UtteranceEnd triggers advance
const SILENCE_WINDOW_SECS   = 30;   // silence escalation fires every N seconds
const MAX_RECORDING_SECS    = 180;  // 3 min → rambling interrupt
const BAR_COUNT             = 20;

// Barge-in
const BARGE_IN_RMS_THRESHOLD = 0.05;
const BARGE_IN_FRAMES        = 5;   // ~83ms at 60fps

// Silence escalation messages (pre-cached in useCartesiaTTS + server.py)
const SILENCE_MESSAGES = {
  1: "I think you may have missed that — let me repeat the question.",
  2: "Take your time, there's no rush.",
  3: "It seems we may have a technical issue. Are you able to hear me?",
  4: "Let's come back to that one — moving on.",
};

// Clarification detection — short, specific phrases to avoid false positives
const CLARIFICATION_KEYWORDS = [
  "can you repeat", "could you repeat", "please repeat",
  "say that again", "say it again",
  "didn't hear you", "couldn't hear", "can't hear",
  "didn't understand", "don't understand", "didn't catch that",
  "come again", "what did you say", "what was that",
  "repeat the question", "missed that",
];

// Ignorance detection — only trigger on short responses
const IGNORANCE_PHRASES = [
  "i don't know", "i do not know",
  "i have no idea", "no idea",
  "i'm not sure", "i am not sure",
  "i cannot answer", "i can't answer",
  "i have no clue", "i'll pass", "i pass",
  "i'd like to skip", "skip this one",
];

// Clarification preambles per tier (pre-cached)
const CLARIFICATION_PREAMBLES = [
  "Let me repeat the question.",   // tier 1
  "Let me rephrase that.",         // tier 2
  "Let me ask it differently.",    // tier 3
];

// Phase 3 — static keyword interceptions (checked on short is_final chunks)
const KEYWORD_AI       = ["are you an ai", "are you a robot", "are you a bot", "are you human", "are you real"];
const KEYWORD_COUNT    = ["how many questions", "questions left", "questions remaining", "how many left", "how many remain"];
const KEYWORD_BREAK    = ["need a break", "can we pause", "need to pause", "take a break", "pause for a moment"];
const KEYWORD_SKIP     = ["skip this question", "skip the question", "can we skip", "can you skip", "i want to skip", "please skip", "skip it please", "move on please", "next question please", "pass this question", "i'll pass", "i pass on this"];
const KEYWORD_FEEDBACK = ["was that a good answer", "how did i do", "was my answer", "am i doing well", "did i answer that"];
const KEYWORD_RECORD   = ["is this being recorded", "will this be recorded", "is this confidential", "is this private"];
const KEYWORD_COUNTER  = ["what do you think about me", "do you think i'm qualified", "would you hire me", "what would you say about me"];
const KEYWORD_SALARY   = ["what is the salary", "what's the salary", "how much does this pay", "what does this role pay", "what is the compensation", "what's the pay for"];

// Mute detection
const MUTE_THRESHOLD   = 0.005;
const MUTE_DETECT_SECS = 15;

// Audio coaching detection — sustained background voice during PHASE.speaking
const COACHING_RMS_THRESHOLD = 0.08;  // above background, below barge-in
const COACHING_SUSTAINED_MS  = 3000;  // must persist 3s to fire

// B5 fix: defined at module level, not inside render
const CHEAT_LABELS = {
  face_missing:    "FACE NOT VISIBLE",
  multiple_faces:  "MULTIPLE PEOPLE DETECTED",
  attention_drift: "ATTENTION DRIFT",
  looking_down:    "EYES LOOKING DOWN",
  looking_away:    "LOOKING AWAY",
  paste_detected:  "PASTE DETECTED",
  audio_coaching:     "BACKGROUND VOICE DETECTED",
  tab_switch:         "TAB SWITCH DETECTED",
  window_blur:        "FOCUS LOST",
  multiple_speakers:  "MULTIPLE SPEAKERS DETECTED",
};

// Video chunk memory cap — ~200 MB prevents browser OOM on long sessions (B7)
const VIDEO_CHUNK_SIZE_CAP = 200 * 1024 * 1024;

const STATUS_LABEL = {
  [PHASE.permissions]: "Setting up your interview…",
  [PHASE.ready]:       "You're all set.",
  [PHASE.speaking]:    "AI speaking…",
  [PHASE.recording]:   "Your turn — speak your answer",
  [PHASE.evaluating]:  "Evaluating your answer…",
  [PHASE.processing]:  "Submitting your answers…",
  [PHASE.delivery]:      "Speech delivery feedback",
  [PHASE.retake_window]: "Re-record window open",
  [PHASE.complete]:      "Interview complete.",
  [PHASE.error]:         "",
};

const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

function ExitConfirmOverlay({ anyAnswered, onStay, onExit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
      <div className="bg-[#111] border border-white/10 p-8 max-w-sm w-full"
           style={{ fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
        <div className="font-mono text-[10px] text-white/40 tracking-[0.25em] mb-3 uppercase">Exit Interview</div>
        <h2 className="font-mono font-bold text-xl text-white mb-2">Leave this session?</h2>
        <p className="text-white/50 text-sm leading-relaxed mb-7">
          {anyAnswered
            ? "Your answered questions will be saved and graded."
            : "No answers have been submitted yet. You'll return to your dashboard."}
        </p>
        <div className="flex gap-3">
          <button onClick={onStay}
            className="flex-1 py-3 border border-white/15 text-white/60 font-mono text-xs uppercase tracking-[0.2em] hover:border-white/30 transition-colors">
            Stay
          </button>
          <button onClick={onExit}
            className="flex-1 py-3 bg-[#E11D48] hover:bg-[#BE123C] text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors">
            {anyAnswered ? "Exit & save" : "Exit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AutoDeliveryCard({ metrics, onAdvance, countdown }) {
  if (!metrics) return null;
  const score = computeDeliveryScore(metrics);
  const scoreColor = score >= 80 ? "text-[#10B981]" : score >= 60 ? "text-[#F59E0B]" : "text-[#E11D48]";
  const items = [
    { key: "wpm",                 label: "PACE",          value: `${metrics.wpm} wpm` },
    { key: "filler_count",        label: "FILLERS",       value: String(metrics.filler_count) },
    { key: "longest_pause_sec",   label: "LONGEST PAUSE", value: `${metrics.longest_pause_sec.toFixed(1)}s` },
    { key: "answer_duration_sec", label: "DURATION",      value: `${metrics.answer_duration_sec}s` },
    { key: "weak_language_count", label: "WEAK PHRASES",  value: String(metrics.weak_language_count) },
  ];
  return (
    <div className="max-w-lg w-full flex flex-col gap-5">
      <div className="text-center">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/30 mb-2">Speech Delivery</div>
        <div className={`text-5xl font-bold tabular-nums font-mono ${scoreColor}`}>{score}</div>
        <div className="font-mono text-[9px] tracking-[0.2em] text-white/20 mt-0.5">/ 100</div>
      </div>
      <div className="flex gap-2 flex-wrap justify-center">
        {items.map(({ key, label, value }) => {
          const color = getMetricColor(key, metrics[key]);
          const cls = COLOR_CLASSES[color];
          return (
            <div key={key} className={`border ${cls.border} ${cls.bg} px-3 py-2.5 min-w-[90px] flex-1`}>
              <div className="font-mono text-[8px] tracking-[0.15em] text-white/25 mb-1">{label}</div>
              <div className={`font-mono text-base font-bold ${cls.text}`}>{value}</div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-col items-center gap-2">
        <div className="w-full h-0.5 bg-white/10 overflow-hidden">
          <div
            className="h-full bg-white/25"
            style={{ width: `${(countdown / 8) * 100}%`, transition: "width 1s linear" }}
          />
        </div>
        <button
          onClick={onAdvance}
          className="px-6 py-2 border border-white/15 text-white/40 hover:text-white/70 hover:border-white/30 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
        >
          Continue ({countdown}s)
        </button>
      </div>
    </div>
  );
}

export default function AutoInterview() {
  const navigate = useNavigate();
  const { id: interviewId, token } = useParams();
  const isGuestMode = !interviewId;

  // Retake URL params (non-guest only)
  const [searchParams] = useSearchParams();
  const retakeModule    = isGuestMode ? null : searchParams.get("retake");
  const qRetakeModule   = isGuestMode ? null : searchParams.get("retake_module");
  const qRetakeQStr     = isGuestMode ? null : searchParams.get("retake_q");
  const qRetakeQIdx     = qRetakeQStr !== null ? parseInt(qRetakeQStr, 10) : null;
  const isRetake        = !!retakeModule;
  const isQuestionRetake = !!qRetakeModule && qRetakeQIdx !== null && !isNaN(qRetakeQIdx);

  const [phase, setPhase]                   = useState(PHASE.permissions);
  const phaseRef                            = useRef(PHASE.permissions);
  const [questions, setQuestions]           = useState([]);
  const [qIndex, setQIndex]                 = useState(0);
  const [transcript, setTranscript]         = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [barHeights, setBarHeights]         = useState(Array(BAR_COUNT).fill(3));
  const [error, setError]                   = useState("");
  const [recSecs, setRecSecs]                   = useState(0);
  const [processingStep, setProcessingStep]     = useState(0);
  const [videoUploadStatus, setVideoUploadStatus] = useState(null); // null | "uploading" | "done" | "failed"
  const [questionVisible, setQuestionVisible]   = useState(true);
  const [followUpCount, setFollowUpCount]       = useState(0);
  const [displayQuestion, setDisplayQuestion]   = useState("");
  const [deliveryData, setDeliveryData]           = useState(null);
  const [deliveryCountdown, setDeliveryCountdown] = useState(8);

  const [exitConfirmOpen, setExitConfirmOpen]       = useState(false);

  // Guest invite metadata — stored for static screens + personalization
  const [inviteInfo, setInviteInfo] = useState(null); // { candidate_name, role_title, company_name }

  // Recording consent — candidate must check this before starting
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [envChecklist, setEnvChecklist] = useState(Array(8).fill(false));

  // Retakes — guest mode only
  const [retakesAllowed, setRetakesAllowed]       = useState(1);
  const [retakesForfeited, setRetakesForfeited]   = useState(false);
  const [retakesUsedState, setRetakesUsedState]   = useState({}); // { [qIdx]: count }

  // Core stable refs
  const questionsRef           = useRef([]);
  const qIndexRef              = useRef(0);
  const transcriptRef          = useRef("");
  const answersRef             = useRef([]);
  const recordingElapsedRef    = useRef(0);
  const utteranceEndHandledRef = useRef(false);
  const nextStepRef            = useRef(null);
  const mountedRef             = useRef(true);

  // Callback ref for video element — re-attaches stream every time the element mounts.
  // Needed because the instructions page (early return) unmounts the video element, so
  // the srcObject set during permissions is lost. The callback re-attaches it on remount.
  const setVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  // Silence escalation
  const silenceSecsRef         = useRef(0);
  const silenceLevelRef        = useRef(0);

  // Phase 2 — per-question state
  const clarificationCountRef  = useRef(0);
  const minimalProbeGivenRef   = useRef(false);
  const ramblingHandledRef     = useRef(false);

  // Phase 3 — mute detection
  const muteSilenceSecsRef     = useRef(0);
  const mutePromptGivenRef     = useRef(false);

  // Media
  const streamRef   = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);

  // STT
  const wsRef             = useRef(null);
  const preWarmWsRef      = useRef(null); // pre-connected STT WebSocket opened during TTS playback
  const mediaRecorderRef  = useRef(null);
  const recordingTimerRef = useRef(null);
  const wsRetryCountRef   = useRef(0);    // persists across retries so the limit is enforced

  // Forward refs — break ordering / circular deps
  const startQuestionRef    = useRef(null);
  const startRecordingRef   = useRef(null);
  const bargeInFramesRef    = useRef(0);
  const handleBargeInRef    = useRef(null);
  const speakingContextRef  = useRef("phrase"); // "question" blocks barge-in; "phrase" allows it
  const handleClarificationRef = useRef(null);
  const handleIgnoranceRef  = useRef(null);
  const handleMinimalRef    = useRef(null);
  const handleRamblingRef   = useRef(null);
  const handleStaticKwRef   = useRef(null);
  const handleMuteRef       = useRef(null);

  // Proctoring — video recording
  const videoRef            = useRef(null);
  const videoRecorderRef    = useRef(null);
  const videoChunksRef        = useRef([]);
  const segmentIndexRef       = useRef(0);
  const segmentStartSecsRef   = useRef(0);
  const interviewStartRef     = useRef(0);
  const segmentIntervalRef    = useRef(null);
  const transcriptBoxRef    = useRef(null);

  // Proctoring — real-time alert (kept for amber indicator trigger, banner removed)
  const [cheatAlert, setCheatAlert]   = useState(null);
  const cheatAlertTimerRef            = useRef(null);
  // Unique signal types captured at submit time for the completion screen
  const [detectedSignalTypes, setDetectedSignalTypes] = useState([]);

  // Session timer (basic plan: 10-min cap)
  const SESSION_LIMIT_SECS       = 10 * 60;  // 600 seconds
  const SESSION_WARN_SECS        = 8 * 60;   // warn at 8 minutes
  const [sessionLimitEnabled, setSessionLimitEnabled] = useState(false);
  const [sessionElapsed, setSessionElapsed]           = useState(0);
  const sessionStartRef   = useRef(null);
  const sessionTimerRef   = useRef(null);

  // Proctoring — audio coaching detection
  const coachingStartRef    = useRef(null);

  // B3: prevent double-invocation of handleBegin
  const hasBegunRef         = useRef(false);

  // B6: stable ref so startWaveform (empty deps) always calls the latest handler
  const handleProctoringEventRef = useRef(null);

  // Follow-up conversation tracking
  const followUpConvRef    = useRef([]);  // conversation history for the active question
  const followUpCountRef   = useRef(0);  // how many follow-ups asked so far
  const callConverseRef    = useRef(null); // forward ref for callConverse

  // Speech delivery tracking (non-guest mode only)
  const fullTranscriptRef    = useRef("");   // aggregated transcript across main + follow-ups
  const totalSpeechSecsRef   = useRef(0);   // accumulated actual speech seconds
  const longestPauseRef      = useRef(0);   // longest gap between utterances
  const speechStartRef       = useRef(null); // Date.now() when latest SpeechStarted fired (pause-gap tracking)
  const sessionFirstSpeechRef = useRef(null); // Date.now() when FIRST SpeechStarted fired this recording session
  const lastSpeechEndRef     = useRef(null); // Date.now() when last UtteranceEnd fired
  const pendingNextIdxRef    = useRef(0);   // question index to advance to after delivery card
  const deliveryAdvanceRef   = useRef(null); // forward ref for handleDeliveryAdvance

  // Retake limit tracking — stable refs so handleRetake closure stays fresh
  const retakesAllowedRef    = useRef(1);
  const retakesForfeitedRef  = useRef(false);
  const retakesUsedRef       = useRef({}); // { [qIdx]: count }

  // Post-AI retake window (guest mode)
  const [retakeWindowSecs, setRetakeWindowSecs]   = useState(0);
  const retakeWindowAdvanceRef                    = useRef(null); // fn to call after window expires

  // Previous attempts — maps qIdx → [{ answer, follow_up_conversation }] for retake tracking
  const previousAttemptsRef = useRef({});

  const { speak, cancel: cancelTTS } = useCartesiaTTS();

  const handleProctoringEvent = useCallback((evt) => {
    // B10: ignore events that fire during phase transitions (phaseRef is always current)
    if (phaseRef.current !== PHASE.speaking && phaseRef.current !== PHASE.recording) return;
    clearTimeout(cheatAlertTimerRef.current);
    setCheatAlert(evt);
    cheatAlertTimerRef.current = setTimeout(() => setCheatAlert(null), 3000);
  }, []);

  // B6: keep ref current so startWaveform's empty-dep closure always reaches latest handler
  useEffect(() => { handleProctoringEventRef.current = handleProctoringEvent; }, [handleProctoringEvent]);

  // Active throughout the interview including retake window — prevents gaps where
  // candidates can consult notes undetected between questions
  const proctoringEnabled = phase === PHASE.speaking || phase === PHASE.recording
    || phase === PHASE.evaluating || phase === PHASE.retake_window;
  const { cheatCount, resetEvents: resetMediaPipeEvents, faceDetected } = useMediaPipeProctoring(
    videoRef,
    { enabled: proctoringEnabled, onEvent: handleProctoringEvent }
  );

  // Browser-level cheat events (tab_switch, window_blur) — tracked outside the MediaPipe hook
  const browserEventsRef               = useRef([]);
  const [browserCheatCount, setBrowserCheatCount] = useState(0);

  const logBrowserCheatEvent = useCallback((type) => {
    if (phaseRef.current !== PHASE.speaking && phaseRef.current !== PHASE.recording) return;
    const evt = { type, at: new Date().toISOString(), duration_sec: 0 };
    browserEventsRef.current = [...browserEventsRef.current, evt];
    setBrowserCheatCount((c) => c + 1);
    clearTimeout(cheatAlertTimerRef.current);
    setCheatAlert(evt);
    cheatAlertTimerRef.current = setTimeout(() => setCheatAlert(null), 3000);
  }, []);

  // Combined reset — merges MediaPipe + browser events and clears both
  const resetAllCheatEvents = useCallback(() => {
    const all = [...resetMediaPipeEvents(), ...browserEventsRef.current];
    browserEventsRef.current = [];
    setBrowserCheatCount(0);
    return all;
  }, [resetMediaPipeEvents]);

  const setPhaseSync = useCallback((p) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  useEffect(() => {
    mountedRef.current = true; // StrictMode runs mount→unmount→remount; re-assert true on each mount
    return () => { mountedRef.current = false; };
  }, []);

  // ── 1. Permissions ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 48000 },
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        // Feed video track to hidden element for MediaPipe face detection
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }

        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        ctx.createMediaStreamSource(stream).connect(analyser);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        setPhaseSync(PHASE.ready);
      } catch {
        if (!cancelled) {
          if (isGuestMode) {
            setPhaseSync(PHASE.camera_required);
          } else {
            setError("Camera and microphone access are required. Please allow them and reload.");
            setPhaseSync(PHASE.error);
          }
        }
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioCtxRef.current?.close();
    };
  }, []); // eslint-disable-line

  // ── 2. Load interview data ──────────────────────────────────────────────
  useEffect(() => {
    if (phase !== PHASE.ready) return;
    (async () => {
      try {
        let flat;
        if (isGuestMode) {
          const { data: inv } = await publicApi.get(`/public/interview/${token}`);
          setInviteInfo({
            candidate_name: inv.candidate_name || "",
            role_title:     inv.role_title     || "",
            company_name:   inv.company_name   || "",
          });
          if (inv.status === "completed") { setPhaseSync(PHASE.already_done); return; }
          if (inv.status === "expired")   { setPhaseSync(PHASE.expired);      return; }
          const allowed   = inv.retakes_per_question ?? 1;
          const forfeited = inv.retakes_forfeited || false;
          retakesAllowedRef.current   = allowed;
          retakesForfeitedRef.current = forfeited;
          setRetakesAllowed(allowed);
          setRetakesForfeited(forfeited);
          flat = (inv.questions || []).map((q, i) => ({ text: q.text, id: q.id, module: null, qIdx: i }));
        } else {
          const { data: iv } = await api.get(`/interviews/${interviewId}`);
          // Redirect to results if already completed (unless it's a retake)
          if (!isRetake && !isQuestionRetake && iv.status === "completed") {
            navigate(`/interviews/${interviewId}/results`);
            return;
          }
          if (isQuestionRetake) {
            const q = iv.modules?.[qRetakeModule]?.questions?.[qRetakeQIdx];
            if (!q) { setError("That question is not part of this interview."); setPhaseSync(PHASE.error); return; }
            flat = [{ text: typeof q === "string" ? q : q.text, id: `${qRetakeModule}-${qRetakeQIdx}`, module: qRetakeModule, qIdx: qRetakeQIdx }];
          } else if (isRetake) {
            if (!iv.modules?.[retakeModule]) { setError("That module is not part of this interview."); setPhaseSync(PHASE.error); return; }
            flat = (iv.modules[retakeModule].questions || []).map((q, qi) => ({
              text: typeof q === "string" ? q : q.text, id: `${retakeModule}-${qi}`, module: retakeModule, qIdx: qi,
            }));
          } else {
            flat = MODULE_ORDER
              .filter(m => iv.modules?.[m])
              .flatMap(m => (iv.modules[m].questions || []).map((q, qi) => ({
                text: typeof q === "string" ? q : q.text,
                id: `${m}-${qi}`,
                module: m,
                qIdx: qi,
              })));
          }
          track("interview_started", { interview_id: interviewId, mode: "voice", is_retake: isRetake || isQuestionRetake, source: iv.source });
          // Load plan to decide whether to enforce the 10-min session timer
          api.get("/candidate/plan-usage").then(({ data }) => {
            if (data?.plan === "basic" && data?.session_limit_minutes) {
              setSessionLimitEnabled(true);
            }
          }).catch(() => {});
        }
        questionsRef.current = flat;
        setQuestions(flat);
      } catch {
        setError("Failed to load interview. Please check your connection and reload.");
        setPhaseSync(PHASE.error);
      }
    })();
  }, [phase]); // eslint-disable-line

  // ── 3. Waveform ─────────────────────────────────────────────────────────
  const stopWaveform = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = null;
    setBarHeights(Array(BAR_COUNT).fill(3));
  }, []);

  const startWaveform = useCallback((mode) => {
    cancelAnimationFrame(animFrameRef.current);
    bargeInFramesRef.current = 0;

    const tick = (t) => {
      // B4: if stopWaveform() was called while this tick was queued, bail out
      if (animFrameRef.current === null) return;

      if (mode === "speaking") {
        const heights = Array.from({ length: BAR_COUNT }, (_, i) => {
          const wave = Math.sin(t * 0.003 + (i / BAR_COUNT) * Math.PI * 2);
          return Math.round(6 + wave * 32);
        });
        setBarHeights(heights);

        // Barge-in + audio coaching: analyse mic during AI TTS
        if (analyserRef.current && phaseRef.current === PHASE.speaking) {
          const td = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteTimeDomainData(td);
          let sumSq = 0;
          for (let i = 0; i < td.length; i++) {
            const v = (td[i] - 128) / 128;
            sumSq += v * v;
          }
          const rms = Math.sqrt(sumSq / td.length);

          if (rms > BARGE_IN_RMS_THRESHOLD && speakingContextRef.current !== "question") {
            // Loud — candidate speaking over AI → barge-in (blocked during question delivery)
            bargeInFramesRef.current += 1;
            coachingStartRef.current = null;
            if (bargeInFramesRef.current >= BARGE_IN_FRAMES) {
              handleBargeInRef.current?.();
              return;
            }
          } else if (isGuestMode && rms > COACHING_RMS_THRESHOLD) {
            // Quieter but above background — possible coaching voice
            bargeInFramesRef.current = 0;
            if (!coachingStartRef.current) {
              coachingStartRef.current = performance.now();
            } else if (performance.now() - coachingStartRef.current >= COACHING_SUSTAINED_MS) {
              coachingStartRef.current = null;
              logBrowserCheatEvent("audio_coaching");
            }
          } else {
            bargeInFramesRef.current = 0;
            coachingStartRef.current = null;
          }
        }
      } else if (mode === "recording" && analyserRef.current) {
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const heights = Array.from({ length: BAR_COUNT }, (_, i) => {
          const idx = Math.floor((i / BAR_COUNT) * data.length);
          return Math.round(3 + (data[idx] / 255) * 80);
        });
        setBarHeights(heights);
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  }, [isGuestMode, logBrowserCheatEvent]);

  // ── 4. Barge-in ─────────────────────────────────────────────────────────
  const handleBargeIn = useCallback(() => {
    if (phaseRef.current !== PHASE.speaking) return;
    cancelTTS();
    stopWaveform();
    setPhaseSync(PHASE.recording);
    silenceLevelRef.current = 0;
    startRecordingRef.current?.();
  }, [cancelTTS, stopWaveform, setPhaseSync]);

  useEffect(() => { handleBargeInRef.current = handleBargeIn; }, [handleBargeIn]);

  // ── 5. STT helpers ──────────────────────────────────────────────────────
  const stopRecording = useCallback(() => {
    clearInterval(recordingTimerRef.current);
    // Flush any in-progress speech time — covers silence-escalation / barge-in paths
    // where stopRecording fires before UtteranceEnd can accumulate the duration.
    if (!isGuestMode && sessionFirstSpeechRef.current != null) {
      totalSpeechSecsRef.current += Math.max(0, (Date.now() - sessionFirstSpeechRef.current) / 1000);
      sessionFirstSpeechRef.current = null;
    }
    if (mediaRecorderRef.current?.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
    // Discard any unused pre-warmed connection
    if (preWarmWsRef.current) {
      try { preWarmWsRef.current.close(); } catch {}
      preWarmWsRef.current = null;
    }
    setInterimTranscript("");
    stopWaveform();
  }, [isGuestMode, stopWaveform]);

  // Opens the backend→Deepgram WebSocket during TTS playback so the connection is
  // ready the moment the candidate starts speaking (eliminates the 1–2s cold-start lag).
  const preWarmSTT = useCallback(() => {
    if (preWarmWsRef.current) {
      try { preWarmWsRef.current.close(); } catch {}
    }
    const ws = new WebSocket(`${WS_BASE}/api/ws/stt`);
    preWarmWsRef.current = ws;
    // No handlers — startRecording() attaches them when it takes ownership
  }, []);

  const startRecording = useCallback(() => {
    transcriptRef.current          = "";
    setTranscript("");
    setInterimTranscript("");
    recordingElapsedRef.current    = 0;
    utteranceEndHandledRef.current = false;
    silenceSecsRef.current         = 0;
    muteSilenceSecsRef.current     = 0;
    // silenceLevelRef is intentionally NOT reset here — silence escalation (levels 1–3)
    // calls startRecording() and must keep the level it just set. Only startQuestion()
    // and explicit fresh-start callers reset it (see each call site below).

    // Reset per-recording delivery markers (totals accumulate across follow-ups; reset in startQuestion)
    if (!isGuestMode) {
      speechStartRef.current        = null;
      sessionFirstSpeechRef.current = null;
      lastSpeechEndRef.current      = null;
    }

    // Transcript message handler — shared by initial connection and all retries
    const onMessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);

        // Any speech fully resets silence state
        if (msg.type === "SpeechStarted") {
          silenceSecsRef.current     = 0;
          silenceLevelRef.current    = 0;
          muteSilenceSecsRef.current = 0;
          // Delivery: record pause gap since last utterance end, then mark speech start
          if (!isGuestMode) {
            if (lastSpeechEndRef.current != null) {
              const gapSecs = (Date.now() - lastSpeechEndRef.current) / 1000;
              if (gapSecs > longestPauseRef.current) longestPauseRef.current = gapSecs;
            }
            // Capture the FIRST burst start per recording session; subsequent bursts
            // don't overwrite so earlier speech time isn't lost when UtteranceEnd fires.
            if (sessionFirstSpeechRef.current == null) {
              sessionFirstSpeechRef.current = Date.now();
            }
            speechStartRef.current = Date.now();
          }
        }

        // Show interim words live as the candidate speaks
        if (msg.type === "Results" && !msg.is_final) {
          const interimText = msg.channel?.alternatives?.[0]?.transcript || "";
          setInterimTranscript(interimText);
        }

        if (msg.type === "Results" && msg.is_final) {
          setInterimTranscript(""); // clear interim once final arrives
          const text = msg.channel?.alternatives?.[0]?.transcript || "";
          if (!text.trim()) return;

          // Diarization: fire signal if ≥5 consecutive words from a non-primary speaker.
          // Threshold is deliberately high: a genuine coaching voice speaks a full
          // sentence, whereas brief background noise / echo / reverb often gets a couple
          // of words misattributed to a second speaker. 5+ words filters those blips out.
          const words = msg.channel?.alternatives?.[0]?.words || [];
          let secondSpeakerRun = 0;
          for (const w of words) {
            if (w.speaker != null && w.speaker > 0) {
              secondSpeakerRun += 1;
              if (secondSpeakerRun >= 5) { logBrowserCheatEvent("multiple_speakers"); break; }
            } else {
              secondSpeakerRun = 0;
            }
          }

          const lower      = text.toLowerCase();
          const chunkWords = text.trim().split(/\s+/).length;

          // ── C2: Clarification detection ────────────────────────────────
          // Only trigger on short chunks to avoid false positives mid-answer
          if (chunkWords < 15 && CLARIFICATION_KEYWORDS.some(k => lower.includes(k))) {
            handleClarificationRef.current?.();
            return; // do not append to transcript
          }

          // ── C3: Ignorance detection ────────────────────────────────────
          // Trigger only when total response is still very short (< 12 words)
          const tentative = (transcriptRef.current + " " + text).trim();
          const totalWords = tentative.split(/\s+/).length;
          if (totalWords < 12 && IGNORANCE_PHRASES.some(k => tentative.toLowerCase().includes(k))) {
            handleIgnoranceRef.current?.();
            return;
          }

          // ── E5-E12: Static keyword interceptions ──────────────────────
          // Only fire on short chunks to avoid false positives mid-answer
          if (chunkWords < 20) {
            if (KEYWORD_AI.some(k => lower.includes(k))) {
              handleStaticKwRef.current?.("I'm an AI interviewer. Let's continue with your interview.");
              return;
            }
            if (KEYWORD_COUNT.some(k => lower.includes(k))) {
              const rem = questionsRef.current.length - qIndexRef.current;
              handleStaticKwRef.current?.(`You have ${rem} question${rem !== 1 ? "s" : ""} remaining, including this one.`);
              return;
            }
            if (KEYWORD_BREAK.some(k => lower.includes(k))) {
              handleStaticKwRef.current?.("Take your time, there's no rush.");
              return;
            }
            if (KEYWORD_SKIP.some(k => lower.includes(k))) {
              handleIgnoranceRef.current?.();
              return;
            }
            if (KEYWORD_FEEDBACK.some(k => lower.includes(k))) {
              handleStaticKwRef.current?.("Let's keep focused on your interview for now.");
              return;
            }
            if (KEYWORD_RECORD.some(k => lower.includes(k))) {
              handleStaticKwRef.current?.("This session is being recorded for evaluation purposes.");
              return;
            }
            if (KEYWORD_COUNTER.some(k => lower.includes(k))) {
              handleStaticKwRef.current?.("I'm here to learn about you — please go ahead and answer the question.");
              return;
            }
            if (KEYWORD_SALARY.some(k => lower.includes(k))) {
              handleStaticKwRef.current?.("Compensation is best discussed directly with the hiring team.");
              return;
            }
          }

          transcriptRef.current = tentative;
          setTranscript(tentative);
        }

        // ── E3: Minimal answer check happens at UtteranceEnd ──────────────
        if (msg.type === "UtteranceEnd") {
          // Delivery: accumulate actual speech duration using FIRST burst start, not last.
          // Subtract utterance_end_ms (5 s) because Deepgram fires UtteranceEnd 5 s after
          // the last speech segment ends — that silence window must not inflate the answer duration.
          if (!isGuestMode && sessionFirstSpeechRef.current != null) {
            const UTTERANCE_END_SECS = 5.0;
            const elapsed = (Date.now() - sessionFirstSpeechRef.current) / 1000;
            totalSpeechSecsRef.current += Math.max(1, elapsed - UTTERANCE_END_SECS);
            lastSpeechEndRef.current    = Date.now();
            speechStartRef.current      = null;
            sessionFirstSpeechRef.current = null;
          }
          if (
            !utteranceEndHandledRef.current &&
            recordingElapsedRef.current >= MIN_ANSWER_SECS &&
            transcriptRef.current.trim().length > 0 &&
            nextStepRef.current
          ) {
            const wordCount = transcriptRef.current.trim().split(/\s+/).length;

            // If answer is too short and we haven't probed yet — probe once
            if (wordCount < 5 && !minimalProbeGivenRef.current) {
              minimalProbeGivenRef.current = true;
              handleMinimalRef.current?.();
              return;
            }

            // Normal advance
            utteranceEndHandledRef.current = true;
            nextStepRef.current();
          }
        }
      } catch {}
    };

    // Always start timer + waveform unconditionally so silence escalation runs
    // even if the STT WebSocket never opens (Deepgram down, bad API key, etc.)
    // Retries only reconnect the WebSocket — they never restart this timer.
    wsRetryCountRef.current = 0;
    clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = setInterval(() => {
        recordingElapsedRef.current += 1;
        silenceSecsRef.current      += 1;

        // ── E2: Rambling interrupt — auto-submit whatever was transcribed ──
        if (!ramblingHandledRef.current && recordingElapsedRef.current >= MAX_RECORDING_SECS) {
          ramblingHandledRef.current    = true;
          utteranceEndHandledRef.current = true; // prevent UtteranceEnd double-advance
          nextStepRef.current?.();
          return; // skip silence escalation this tick
        }

        // ── Silence escalation ─────────────────────────────────────────
        const secs  = silenceSecsRef.current;
        const level = silenceLevelRef.current;
        if (!utteranceEndHandledRef.current && level < 4 && secs >= SILENCE_WINDOW_SECS) {
          const nextLevel = level + 1;
          silenceLevelRef.current = nextLevel;
          silenceSecsRef.current  = 0;
          (async () => {
            stopRecording();
            if (!mountedRef.current) return;
            setPhaseSync(PHASE.speaking);
            startWaveform("speaking");
            if (nextLevel === 4) {
              await speak(SILENCE_MESSAGES[4]);
              if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
              stopWaveform();
              const q = questionsRef.current[qIndexRef.current];
              if (followUpCountRef.current > 0) {
                // Inside a follow-up chain — rescue the last candidate answer rather than discarding everything
                const conv = followUpConvRef.current;
                const lastCandidateTurn = [...conv].reverse().find(t => t.role === "candidate");
                const savedText = lastCandidateTurn?.text || "[No answer recorded]";
                answersRef.current = [...answersRef.current, { q, text: savedText, follow_up_conversation: conv }];
              } else {
                answersRef.current = [...answersRef.current, { q, text: "[Skipped — no response]", skipped: true }];
              }
              const next = qIndexRef.current + 1;
              if (next < questionsRef.current.length) { startQuestionRef.current?.(next); }
              else { setPhaseSync(PHASE.processing); }
              return;
            }
            await speak(SILENCE_MESSAGES[nextLevel]);
            if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
            if (nextLevel === 1) {
              speakingContextRef.current = "question";
              await speak(questionsRef.current[qIndexRef.current]?.text || "");
              speakingContextRef.current = "phrase";
              if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
            }
            stopWaveform();
            setPhaseSync(PHASE.recording);
            silenceSecsRef.current = 0;
            startRecordingRef.current?.();
          })();
        }
        // ── E13: Mute detection ────────────────────────────────────────
        if (analyserRef.current && !utteranceEndHandledRef.current && !mutePromptGivenRef.current) {
          const td = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteTimeDomainData(td);
          let sumSq = 0;
          for (let i = 0; i < td.length; i++) {
            const v = (td[i] - 128) / 128;
            sumSq += v * v;
          }
          if (Math.sqrt(sumSq / td.length) < MUTE_THRESHOLD) {
            muteSilenceSecsRef.current += 1;
            if (muteSilenceSecsRef.current >= MUTE_DETECT_SECS) {
              mutePromptGivenRef.current = true;
              handleMuteRef.current?.();
            }
          } else {
            muteSilenceSecsRef.current = 0;
          }
        }
    }, 1000);

    startWaveform("recording");

    // connectSTT: (re)connects the WebSocket + MediaRecorder only.
    // Called on initial start AND on retries. Never resets the silence timer.
    const connectSTT = () => {
      if (mediaRecorderRef.current?.state === "recording") {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }

      // Reuse pre-warmed connection if still live; otherwise open fresh
      const existing = preWarmWsRef.current;
      const ws = (existing && existing.readyState <= WebSocket.OPEN)
        ? existing
        : new WebSocket(`${WS_BASE}/api/ws/stt`);
      preWarmWsRef.current = null;
      wsRef.current = ws;

      const beginCapture = () => {
        try {
          const audioTracks = streamRef.current?.getAudioTracks() ?? [];
          const audioStream = audioTracks.length > 0 ? new MediaStream(audioTracks) : streamRef.current;
          const audioMime = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/ogg", "audio/mp4"]
            .find(t => MediaRecorder.isTypeSupported(t)) || "";
          const recorder = new MediaRecorder(audioStream, audioMime ? { mimeType: audioMime } : {});
          mediaRecorderRef.current = recorder;
          recorder.ondataavailable = (e) => {
            if (wsRef.current?.readyState === WebSocket.OPEN && e.data.size > 0) {
              wsRef.current.send(e.data);
            }
          };
          recorder.start(250);
        } catch (err) {
          console.warn("[stt] AudioRecorder start failed:", err);
        }
      };

      if (ws.readyState === WebSocket.OPEN) {
        beginCapture();
      } else {
        ws.onopen = beginCapture;
      }

      // B2 + B9: on error/unexpected close, retry up to 2 times, then force-advance.
      // Retries only reconnect the WebSocket — they never restart the silence timer.
      const handleWsFailure = () => {
        if (phaseRef.current !== PHASE.recording) return;
        if (mediaRecorderRef.current?.state === "recording") {
          try { mediaRecorderRef.current.stop(); } catch {}
        }
        if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
        wsRetryCountRef.current += 1;
        if (wsRetryCountRef.current > 2) {
          // STT permanently unavailable — force-advance immediately rather than
          // making the candidate wait 4 × 30 s for silence escalation level 4.
          console.warn("[stt] WebSocket unavailable after %d retries — force-advancing", wsRetryCountRef.current - 1);
          stopRecording(); // clear silence-escalation timer
          if (!mountedRef.current) return;
          const q   = questionsRef.current[qIndexRef.current];
          const next = qIndexRef.current + 1;
          if (followUpCountRef.current > 0) {
            // Inside a follow-up chain: rescue the last candidate answer
            const conv = followUpConvRef.current;
            const lastCandidate = [...conv].reverse().find(t => t.role === "candidate");
            answersRef.current = [
              ...answersRef.current,
              { q, text: lastCandidate?.text || "[No answer recorded]", follow_up_conversation: conv },
            ];
          } else {
            // Main question STT failed: force follow_up_count to max so callConverse
            // cannot trigger another follow-up loop, then call converse with what we have.
            followUpCountRef.current = 1;
            const answer = transcriptRef.current.trim() || "[No answer recorded]";
            callConverseRef.current?.(q, answer, qIndexRef.current);
            return; // callConverse handles the advance
          }
          if (next < questionsRef.current.length) startQuestionRef.current?.(next);
          else setPhaseSync(PHASE.processing);
          return;
        }
        setTimeout(() => {
          if (!mountedRef.current || phaseRef.current !== PHASE.recording) return;
          connectSTT();
        }, 1000);
      };

      ws.onmessage = onMessage;
      ws.onerror   = handleWsFailure;
      ws.onclose   = (evt) => { if (evt.code !== 1000) handleWsFailure(); };
    };

    connectSTT();
  }, [isGuestMode, speak, startWaveform, stopWaveform, stopRecording, setPhaseSync, logBrowserCheatEvent]);

  // ── 6. Phase 2 handlers ─────────────────────────────────────────────────

  // C2 — Clarification: stop, speak preamble + question, resume
  const handleClarification = useCallback(async () => {
    const tier = Math.min(clarificationCountRef.current, 2);
    clarificationCountRef.current += 1;
    stopRecording();
    if (!mountedRef.current) return;
    setPhaseSync(PHASE.speaking);
    startWaveform("speaking");
    await speak(CLARIFICATION_PREAMBLES[tier]);
    if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
    // Use the last interviewer turn in the conversation — this is the follow-up question
    // when inside a follow-up chain, or the original question otherwise.
    const conv = followUpConvRef.current;
    const lastInterviewerTurn = [...conv].reverse().find(t => t.role === "interviewer");
    speakingContextRef.current = "question";
    await speak(lastInterviewerTurn?.text || questionsRef.current[qIndexRef.current]?.text || "");
    speakingContextRef.current = "phrase";
    if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
    stopWaveform();
    setPhaseSync(PHASE.recording);
    silenceSecsRef.current = 0;
    silenceLevelRef.current = 0;
    startRecordingRef.current?.();
  }, [speak, stopRecording, startWaveform, stopWaveform, setPhaseSync]);

  // C3 — Ignorance / skip: acknowledge then advance.
  // If we're inside a follow-up chain the candidate already answered the main question —
  // rescue those answers instead of discarding everything and show the retake window.
  const handleIgnorance = useCallback(async () => {
    stopRecording();
    if (!mountedRef.current) return;
    setPhaseSync(PHASE.speaking);
    startWaveform("speaking");
    await speak("No problem at all, let's move on.");
    if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
    stopWaveform();
    const q    = questionsRef.current[qIndexRef.current];
    const qIdx = qIndexRef.current;
    const next = qIdx + 1;
    const isLast = next >= questionsRef.current.length;

    if (followUpCountRef.current > 0) {
      // Candidate answered the main question but skipped this follow-up.
      // Rescue the last real answer from the conversation rather than discarding everything.
      const conv = followUpConvRef.current;
      const lastCandidate = [...conv].reverse().find(t => t.role === "candidate");
      answersRef.current = [
        ...answersRef.current,
        { q, text: lastCandidate?.text || "[No answer recorded]", follow_up_conversation: conv },
      ];
      // Show retake window if retakes remain (guest mode only)
      if (isGuestMode) {
        const allowed = retakesAllowedRef.current;
        const used    = retakesUsedRef.current[qIdx] || 0;
        if (allowed > 0 && used < allowed && !retakesForfeitedRef.current) {
          setDisplayQuestion(questionsRef.current[qIdx]?.text || "");
          retakeWindowAdvanceRef.current = () => {
            if (isLast) setPhaseSync(PHASE.processing);
            else startQuestionRef.current?.(next);
          };
          setPhaseSync(PHASE.retake_window);
          return;
        }
      }
    } else {
      // Not in a follow-up — standard unable-to-answer mark
      answersRef.current = [...answersRef.current, { q, text: "[Unable to answer]", ignorance: true }];
    }

    if (!isLast) { startQuestionRef.current?.(next); }
    else { setPhaseSync(PHASE.processing); }
  }, [isGuestMode, speak, stopRecording, startWaveform, stopWaveform, setPhaseSync]);

  // E3 — Minimal answer probe: ask for an example, continue recording
  const handleMinimal = useCallback(async () => {
    stopRecording();
    if (!mountedRef.current) return;
    setPhaseSync(PHASE.speaking);
    startWaveform("speaking");
    await speak("Could you walk me through a specific example?");
    if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
    stopWaveform();
    setPhaseSync(PHASE.recording);
    silenceSecsRef.current = 0;
    silenceLevelRef.current = 0;
    startRecordingRef.current?.();
  }, [speak, stopRecording, startWaveform, stopWaveform, setPhaseSync]);

  // E5-E12 — generic "stop, speak, resume" for static keyword responses
  const handleStaticKeyword = useCallback(async (responseText) => {
    stopRecording();
    if (!mountedRef.current) return;
    setPhaseSync(PHASE.speaking);
    startWaveform("speaking");
    await speak(responseText);
    if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
    stopWaveform();
    setPhaseSync(PHASE.recording);
    silenceSecsRef.current     = 0;
    silenceLevelRef.current    = 0;
    muteSilenceSecsRef.current = 0;
    startRecordingRef.current?.();
  }, [speak, stopRecording, startWaveform, stopWaveform, setPhaseSync]);

  // E13 — mute detection: prompt once, then resume
  const handleMute = useCallback(async () => {
    if (phaseRef.current !== PHASE.recording) return;
    stopRecording();
    if (!mountedRef.current) return;
    setPhaseSync(PHASE.speaking);
    startWaveform("speaking");
    await speak("It looks like you might be muted — could you check your microphone?");
    if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
    stopWaveform();
    setPhaseSync(PHASE.recording);
    silenceSecsRef.current     = 0;
    silenceLevelRef.current    = 0;
    muteSilenceSecsRef.current = 0;
    startRecordingRef.current?.();
  }, [speak, stopRecording, startWaveform, stopWaveform, setPhaseSync]);

  // Keep all forward refs in sync
  useEffect(() => { handleBargeInRef.current       = handleBargeIn;       }, [handleBargeIn]);
  useEffect(() => { handleClarificationRef.current = handleClarification; }, [handleClarification]);
  useEffect(() => { handleIgnoranceRef.current     = handleIgnorance;     }, [handleIgnorance]);
  useEffect(() => { handleMinimalRef.current       = handleMinimal;       }, [handleMinimal]);
  useEffect(() => { handleStaticKwRef.current      = handleStaticKeyword; }, [handleStaticKeyword]);
  useEffect(() => { handleMuteRef.current          = handleMute;          }, [handleMute]);

  // ── 6b. Follow-up converse ───────────────────────────────────────────────
  const callConverse = useCallback(async (q, latestAnswer, qIdx) => {
    const convHistory = followUpConvRef.current;
    const fuCount     = followUpCountRef.current;

    // Append candidate's answer to history before sending
    const updatedConv = [...convHistory, { role: "candidate", text: latestAnswer }];
    followUpConvRef.current = updatedConv;

    // Accumulate full transcript for delivery analysis (non-guest only)
    if (!isGuestMode) {
      fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + " " : "") + latestAnswer;
    }

    setPhaseSync(PHASE.evaluating);
    console.log("[converse] start qIdx=%d fuCount=%d answer=%s", qIdx, fuCount, latestAnswer.slice(0, 40));

    try {
      let result;
      if (isGuestMode) {
        const { data } = await publicApi.post(`/public/interview/${token}/converse`, {
          question_text: q.text,
          latest_answer: latestAnswer,
          conversation_history: convHistory,
          follow_up_count: fuCount,
          max_follow_ups: 1,
          voice_mode: true,
        }, { timeout: 15000 });
        result = data;
      } else {
        const { data } = await api.post(`/interviews/${interviewId}/converse`, {
          module: q.module,
          question_index: q.qIdx,
          conversation_history: convHistory,
          latest_answer: latestAnswer,
          follow_up_count: fuCount,
          max_follow_ups: 1,
          voice_mode: true,
        }, { timeout: 15000 });
        result = data;
      }

      console.log("[converse] result=%o mounted=%s phase=%s", result, mountedRef.current, phaseRef.current);
      if (!mountedRef.current) return;

      if (result.action === "follow_up" && result.follow_up_text && fuCount < 1) {
        // Append interviewer follow-up to history
        const newConv = [...updatedConv, { role: "interviewer", text: result.follow_up_text }];
        followUpConvRef.current   = newConv;
        followUpCountRef.current  = fuCount + 1;
        setFollowUpCount(fuCount + 1);
        setDisplayQuestion(result.follow_up_text);

        // Speak follow-up then listen again (nextStepRef unchanged — will call callConverse again)
        setPhaseSync(PHASE.speaking);
        startWaveform("speaking");
        preWarmSTT(); // pre-connect while follow-up is being spoken
        speakingContextRef.current = "question";
        await speak(result.follow_up_text);
        speakingContextRef.current = "phrase";
        if (!mountedRef.current || phaseRef.current !== PHASE.speaking) return;
        stopWaveform();
        setPhaseSync(PHASE.recording);
        silenceSecsRef.current     = 0;
        silenceLevelRef.current    = 0;
        muteSilenceSecsRef.current = 0;
        ramblingHandledRef.current = false; // re-arm 3-min cap for each follow-up recording
        startRecordingRef.current?.();
      } else {
        // No follow-up — save the full conversation and optionally show delivery card.
        // If we're inside a follow-up chain and the latest text is a skip phrase that slipped
        // through keyword detection, rescue the last real candidate answer from the conversation
        // so the grader sees the main answer, not "can you skip the question please".
        let answerText = latestAnswer;
        if (fuCount > 0) {
          const skipSignals = ["skip", "move on", "next question", "pass this", "pass on this"];
          const words = latestAnswer.trim().split(/\s+/);
          const looksLikeSkip = words.length < 20 && skipSignals.some(s => latestAnswer.toLowerCase().includes(s));
          if (looksLikeSkip) {
            const prevCandidate = [...updatedConv].reverse().find((t, i) => t.role === "candidate" && i > 0);
            if (prevCandidate?.text) answerText = prevCandidate.text;
          }
        }
        const savedAnswer = { q, text: answerText, follow_up_conversation: updatedConv };

        if (!isGuestMode) {
          const deliveryMetrics = computeDelivery(
            fullTranscriptRef.current,
            totalSpeechSecsRef.current,
            longestPauseRef.current
          );
          console.log("[converse] delivery metrics=%o speechSecs=%s transcript=%s", deliveryMetrics, totalSpeechSecsRef.current, fullTranscriptRef.current.slice(0, 40));
          if (deliveryMetrics) {
            savedAnswer.delivery = deliveryMetrics;
            pendingNextIdxRef.current = qIdx + 1;
            answersRef.current = [...answersRef.current, savedAnswer];
            setDeliveryData(deliveryMetrics);
            setPhaseSync(PHASE.delivery);
            console.log("[converse] → PHASE.delivery");
            return;
          }
        }

        answersRef.current = [...answersRef.current, savedAnswer];
        const next   = qIdx + 1;
        const isLast = next >= questionsRef.current.length;
        console.log("[converse] → next=%d isLast=%s", next, isLast);

        // Guest mode: open post-AI retake window if retakes remain
        if (isGuestMode) {
          const allowed = retakesAllowedRef.current;
          const used    = retakesUsedRef.current[qIdx] || 0;
          if (allowed > 0 && used < allowed && !retakesForfeitedRef.current) {
            setDisplayQuestion(questionsRef.current[qIdx]?.text || "");
            retakeWindowAdvanceRef.current = () => {
              if (isLast) setPhaseSync(PHASE.processing);
              else startQuestionRef.current?.(next);
            };
            setPhaseSync(PHASE.retake_window);
            return;
          }
        }

        if (!isLast) { startQuestionRef.current?.(next); }
        else { setPhaseSync(PHASE.processing); }
      }
    } catch (err) {
      console.error("[converse] error:", err?.message || err);
      answersRef.current = [
        ...answersRef.current,
        { q, text: latestAnswer, follow_up_conversation: updatedConv },
      ];
      const next = qIdx + 1;
      if (next < questionsRef.current.length) {
        console.log("[converse] catch → startQuestion(%d)", next);
        startQuestionRef.current?.(next);
        // Belt-and-suspenders: if the ref is gone, at least leave recording state
        if (!startQuestionRef.current) setPhaseSync(PHASE.recording);
      } else {
        setPhaseSync(PHASE.processing);
      }
    }
  }, [isGuestMode, token, interviewId, speak, startWaveform, stopWaveform, setPhaseSync, preWarmSTT]);

  useEffect(() => { callConverseRef.current = callConverse; }, [callConverse]);

  const handleDeliveryAdvance = useCallback(() => {
    const nextIdx = pendingNextIdxRef.current;
    if (nextIdx < questionsRef.current.length) { startQuestionRef.current?.(nextIdx); }
    else { setPhaseSync(PHASE.processing); }
  }, [setPhaseSync]);

  useEffect(() => { deliveryAdvanceRef.current = handleDeliveryAdvance; }, [handleDeliveryAdvance]);

  // ── 7. Core question flow ───────────────────────────────────────────────
  // Upload a single 30-second segment with retries.
  // isFinal=true shows the completion-screen spinner and updates videoUploadStatus.
  const uploadSegmentBackground = useCallback(async (segIdx, startSecs, chunks, isFinal = false) => {
    if (isFinal) setVideoUploadStatus("uploading");
    const blob = new Blob(chunks, { type: "video/webm" });
    const DELAYS = [5000, 15000, 30000];
    for (let attempt = 0; attempt < 4; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, DELAYS[attempt - 1]));
      try {
        await publicApi.post(
          `/public/interview/${token}/upload-segment?segment_index=${segIdx}&segment_start_secs=${startSecs.toFixed(2)}`,
          blob,
          { headers: { "Content-Type": "video/webm" }, timeout: 120000 }
        );
        console.log("[video] segment %d uploaded ok (attempt %d)", segIdx, attempt + 1);
        if (isFinal) setVideoUploadStatus("done");
        return;
      } catch (err) {
        console.warn("[video] segment %d attempt %d/4 failed: %s", segIdx, attempt + 1, err?.message || err);
      }
    }
    console.error("[video] segment %d all attempts exhausted", segIdx);
    if (isFinal) setVideoUploadStatus("failed");
  }, [token]);

  // Stop current recorder, upload accumulated chunks as a segment, start fresh recorder.
  // Called every 30s by the segment rotation interval.
  const rotateSegment = useCallback(() => {
    const recorder = videoRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;

    const segIdx    = segmentIndexRef.current;
    const startSecs = segmentStartSecsRef.current;
    segmentIndexRef.current    += 1;
    segmentStartSecsRef.current = (Date.now() - interviewStartRef.current) / 1000;

    recorder.onstop = () => {
      if (!mountedRef.current) return;
      const chunks = [...videoChunksRef.current];
      videoChunksRef.current = [];

      // Start fresh recorder immediately — new header, independently decodable segment
      try {
        const newRec = new MediaRecorder(streamRef.current, { mimeType: recorder.mimeType });
        newRec.ondataavailable = (e) => {
          if (!e.data?.size) return;
          const used = videoChunksRef.current.reduce((s, c) => s + c.size, 0);
          if (used < VIDEO_CHUNK_SIZE_CAP) videoChunksRef.current.push(e.data);
        };
        newRec.start(1000);
        videoRecorderRef.current = newRec;
      } catch (err) {
        console.warn("[video] recorder restart failed:", err?.message);
      }

      if (chunks.length > 0) {
        console.log("[video] rotating segment %d (%.1fs – %.1fs) %d chunks", segIdx, startSecs, segmentStartSecsRef.current, chunks.length);
        uploadSegmentBackground(segIdx, startSecs, chunks);
      }
    };

    recorder.stop();
  }, [uploadSegmentBackground]);

  const submitAllRef = useRef(null);
  const submitAll = useCallback(async () => {
    try {
      const allAnswers = answersRef.current;
      if (isGuestMode) {
        // Stop rotation interval — no more auto-rotations
        clearInterval(segmentIntervalRef.current);
        segmentIntervalRef.current = null;

        // Stop recorder and collect the final partial segment
        if (videoRecorderRef.current?.state === "recording") {
          await new Promise((res) => {
            videoRecorderRef.current.onstop = res;
            videoRecorderRef.current.stop();
          });
        }
        videoRecorderRef._pasteCleanup?.();

        // Snapshot final segment (recorder.onstop flushed any remaining data above)
        const finalChunks    = [...videoChunksRef.current];
        const finalSegIdx    = segmentIndexRef.current;
        const finalStartSecs = segmentStartSecsRef.current;
        videoChunksRef.current = [];

        const cheatEvents = resetAllCheatEvents();
        setDetectedSignalTypes([...new Set(cheatEvents.map(e => e.type))]);

        // Step 1: Evaluate + save — score is never blocked by video upload
        await publicApi.post(`/public/interview/${token}/complete`, {
          answers: allAnswers
            .filter(a => !a.skipped && !a.ignorance)
            .map(a => {
              const prev = previousAttemptsRef.current[a.q.qIdx];
              return {
                q_id: a.q.id,
                q_index: a.q.qIdx,
                text: a.text,
                follow_up_conversation: a.follow_up_conversation || [],
                ...(prev?.length > 0 && { previous_attempts: prev }),
              };
            }),
          cheat_events: cheatEvents,
        });
        setPhaseSync(PHASE.complete);

        // Step 2 (background): upload the final partial segment with retries
        if (finalChunks.length > 0) {
          console.log("[video] uploading final segment %d (%.1fs) %d chunks", finalSegIdx, finalStartSecs, finalChunks.length);
          uploadSegmentBackground(finalSegIdx, finalStartSecs, finalChunks, true);
        } else {
          console.log("[video] no final chunks to upload");
        }
      } else {
        const postAnswer = async (a) => {
          const isSkipped = !!(a.skipped || a.ignorance);
          await api.post(`/interviews/${interviewId}/answer`, {
            module: a.q.module,
            question_index: a.q.qIdx,
            answer_text: isSkipped ? "[Skipped]" : a.text,
            skipped: isSkipped,
            follow_up_conversation: a.follow_up_conversation || [],
            ...(a.delivery && !isSkipped && { delivery: a.delivery }),
          });
        };

        if (isQuestionRetake) {
          const a = allAnswers[0];
          if (a && !a.skipped && !a.ignorance) {
            await api.post(`/interviews/${interviewId}/retake-question`, {
              module: qRetakeModule, q_index: qRetakeQIdx, answer: a.text,
            });
          }
        } else if (isRetake) {
          for (const a of allAnswers) await postAnswer(a);
          await api.post(`/interviews/${interviewId}/grade-module`, { module: retakeModule });
        } else {
          for (const a of allAnswers) await postAnswer(a);
          const cheatEvents = resetAllCheatEvents();
          await api.post(`/interviews/${interviewId}/finish`, { cheat_events: cheatEvents });
        }
        navigate(`/interviews/${interviewId}/results`);
      }
    } catch {
      setError("Failed to submit answers. Please check your connection and reload.");
      setPhaseSync(PHASE.error);
    }
  }, [isGuestMode, token, interviewId, navigate, setPhaseSync, resetAllCheatEvents, uploadSegmentBackground, isRetake, isQuestionRetake, retakeModule, qRetakeModule, qRetakeQIdx]);
  // Keep stable ref so the timer effect can call it without stale closure
  submitAllRef.current = submitAll;

  const startQuestion = useCallback((idx) => {
    if (idx >= questionsRef.current.length) {
      setPhaseSync(PHASE.processing);
      return;
    }

    qIndexRef.current = idx;
    setQIndex(idx);

    // Reset all per-question state
    silenceSecsRef.current        = 0;
    silenceLevelRef.current       = 0;
    clarificationCountRef.current = 0;
    minimalProbeGivenRef.current  = false;
    ramblingHandledRef.current    = false;
    muteSilenceSecsRef.current    = 0;
    mutePromptGivenRef.current    = false;

    const q = questionsRef.current[idx];

    // Reset follow-up state for this question
    followUpConvRef.current  = [{ role: "interviewer", text: q.text }];
    followUpCountRef.current = 0;
    setFollowUpCount(0);
    setDisplayQuestion(q.text);

    // Reset delivery tracking for this question (non-guest only)
    if (!isGuestMode) {
      fullTranscriptRef.current        = "";
      totalSpeechSecsRef.current       = 0;
      longestPauseRef.current          = 0;
      speechStartRef.current           = null;
      sessionFirstSpeechRef.current    = null;
      lastSpeechEndRef.current         = null;
    }

    // Route answer completion through callConverse (handles follow-up cycle)
    nextStepRef.current = () => {
      stopRecording();
      const answer = transcriptRef.current.trim() || "[No answer recorded]";
      callConverseRef.current?.(q, answer, idx);
    };

    (async () => {
      setPhaseSync(PHASE.speaking);
      startWaveform("speaking");
      preWarmSTT(); // open backend→Deepgram connection while question is being spoken
      speakingContextRef.current = "question";
      await speak(q.text);
      speakingContextRef.current = "phrase";
      if (phaseRef.current !== PHASE.speaking) return;
      stopWaveform();
      setPhaseSync(PHASE.recording);
      startRecording();
    })();
  }, [isGuestMode, speak, startRecording, stopRecording, startWaveform, stopWaveform, setPhaseSync, preWarmSTT]);

  // Keep forward refs in sync
  useEffect(() => { startQuestionRef.current  = startQuestion;  }, [startQuestion]);
  useEffect(() => { startRecordingRef.current = startRecording; }, [startRecording]);

  useEffect(() => {
    if (phase === PHASE.processing) submitAll();
  }, [phase, submitAll]);

  // Block tab-close while video is uploading in the background — score is safe but recording would be lost.
  useEffect(() => {
    if (videoUploadStatus !== "uploading") return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [videoUploadStatus]);

  // Safety net: if stuck in evaluating >20s something silently failed — force advance
  useEffect(() => {
    if (phase !== PHASE.evaluating) return;
    const id = setTimeout(() => {
      if (phaseRef.current !== PHASE.evaluating) return;
      console.warn("[AutoInterview] evaluating stuck 20s — forcing advance");
      const next = qIndexRef.current + 1;
      if (next < questionsRef.current.length) startQuestionRef.current?.(next);
      else setPhaseSync(PHASE.processing);
    }, 20000);
    return () => clearTimeout(id);
  }, [phase, setPhaseSync]);

  // Auto-scroll transcript box to bottom as words arrive
  useEffect(() => {
    if (transcriptBoxRef.current) {
      transcriptBoxRef.current.scrollTop = transcriptBoxRef.current.scrollHeight;
    }
  }, [transcript]);

  // ── 8. User actions ─────────────────────────────────────────────────────
  const handleBegin = useCallback(async () => {
    // B3: prevent double-invocation from rapid clicks
    if (hasBegunRef.current) return;
    if (questionsRef.current.length === 0) return;
    hasBegunRef.current = true;
    answersRef.current = [];

    if (isGuestMode) {
      publicApi.post(`/public/interview/${token}/start`).catch(() => {});

      // Start full-session video recording for proctoring
      if (streamRef.current) {
        videoChunksRef.current = [];
        const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
          ? "video/webm;codecs=vp9,opus"
          : "video/webm";
        try {
          const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
          rec.ondataavailable = (e) => {
            if (!e.data?.size) return;
            // B7: enforce 200MB memory cap — stop recording chunks beyond the limit
            const used = videoChunksRef.current.reduce((s, c) => s + c.size, 0);
            if (used < VIDEO_CHUNK_SIZE_CAP) videoChunksRef.current.push(e.data);
          };
          rec.start(1000);
          videoRecorderRef.current = rec;
          console.log("[video] recorder started, mime=%s", mime);

          // Initialize segment tracking and start 30s rotation interval
          interviewStartRef.current     = Date.now();
          segmentIndexRef.current       = 0;
          segmentStartSecsRef.current   = 0;
          segmentIntervalRef.current    = setInterval(() => {
            if (mountedRef.current) rotateSegment();
          }, 30000);
        } catch (err) {
          console.warn("[video] recorder start failed:", err?.message || err);
        }
      }

      // B11: remove any previously registered paste listener before adding a new one
      videoRecorderRef._pasteCleanup?.();
      const onPaste = () => {
        logBrowserCheatEvent("paste_detected");
      };
      document.addEventListener("paste", onPaste);
      videoRecorderRef._pasteCleanup = () => document.removeEventListener("paste", onPaste);

      // Personalised AI welcome before first question
      setPhaseSync(PHASE.speaking);
      speakingContextRef.current = "phrase";
      const firstName = inviteInfo?.candidate_name?.split(" ")[0] || "there";
      let welcome = `Hi ${firstName}! I'm your AI interviewer`;
      if (inviteInfo?.role_title && inviteInfo?.company_name) {
        welcome += ` for the ${inviteInfo.role_title} position at ${inviteInfo.company_name}.`;
      } else if (inviteInfo?.role_title) {
        welcome += ` for the ${inviteInfo.role_title} position.`;
      } else if (inviteInfo?.company_name) {
        welcome += ` at ${inviteInfo.company_name}.`;
      } else {
        welcome += ` today.`;
      }
      welcome += ` Let's get started.`;
      await speak(welcome);
      if (!mountedRef.current) return;
    }

    startQuestion(0);
  }, [isGuestMode, token, startQuestion, logBrowserCheatEvent, speak, inviteInfo, setPhaseSync, rotateSegment]);

  const handleRetake = useCallback(() => {
    if (!isGuestMode) return;

    if (phaseRef.current === PHASE.retake_window) {
      // Post-AI window retake
      const qIdx    = qIndexRef.current;
      const allowed = retakesAllowedRef.current;
      const used    = retakesUsedRef.current[qIdx] || 0;
      if (retakesForfeitedRef.current || allowed <= 0 || used >= allowed) return;
      const newUsed = { ...retakesUsedRef.current, [qIdx]: used + 1 };
      retakesUsedRef.current = newUsed;
      setRetakesUsedState(newUsed);
      // Save the displaced answer as a previous attempt before removing it
      const displaced = answersRef.current[answersRef.current.length - 1];
      if (displaced) {
        const existing = previousAttemptsRef.current[qIdx] || [];
        previousAttemptsRef.current = {
          ...previousAttemptsRef.current,
          [qIdx]: [...existing, { answer: displaced.text || "", follow_up_conversation: displaced.follow_up_conversation || [] }],
        };
      }
      answersRef.current = answersRef.current.slice(0, -1);
      cancelTTS();
      startQuestion(qIdx);
    }
  }, [isGuestMode, cancelTTS, startQuestion]);

  // ── Exit / partial save (non-guest only) ────────────────────────────────
  const performExit = useCallback(async () => {
    stopRecording();
    cancelTTS();
    setExitConfirmOpen(false);
    const answered = answersRef.current;
    if (!isGuestMode && answered.length > 0) {
      try {
        for (const a of answered) {
          const isSkipped = !!(a.skipped || a.ignorance);
          await api.post(`/interviews/${interviewId}/answer`, {
            module: a.q.module,
            question_index: a.q.qIdx,
            answer_text: isSkipped ? "[Skipped]" : a.text,
            skipped: isSkipped,
            follow_up_conversation: a.follow_up_conversation || [],
            ...(a.delivery && !isSkipped && { delivery: a.delivery }),
          });
        }
        if (!isRetake && !isQuestionRetake) {
          await api.post(`/interviews/${interviewId}/finish`, { cheat_events: resetAllCheatEvents() });
        }
      } catch {}
      navigate(`/interviews/${interviewId}/results`);
    } else {
      navigate("/dashboard");
    }
  }, [isGuestMode, interviewId, isRetake, isQuestionRetake, stopRecording, cancelTTS, navigate, resetAllCheatEvents]);

  // Browser visibility / focus proctoring (tab_switch + window_blur)
  useEffect(() => {
    if (!proctoringEnabled) return;
    const onVisChange = () => {
      if (document.visibilityState === "hidden") logBrowserCheatEvent("tab_switch");
    };
    const onBlur = () => logBrowserCheatEvent("window_blur");
    document.addEventListener("visibilitychange", onVisChange);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisChange);
      window.removeEventListener("blur", onBlur);
    };
  }, [proctoringEnabled, logBrowserCheatEvent]);

  // ── 8b. Session timer — basic plan 10-min cap ───────────────────────────
  useEffect(() => {
    if (!sessionLimitEnabled) return;
    const activePhases = [PHASE.speaking, PHASE.recording, PHASE.evaluating];
    const isActive = activePhases.includes(phase);
    if (!isActive) return;
    // Start timer on first active phase entry
    if (!sessionStartRef.current) sessionStartRef.current = Date.now();
    clearInterval(sessionTimerRef.current);
    sessionTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      setSessionElapsed(elapsed);
      if (elapsed >= SESSION_LIMIT_SECS) {
        clearInterval(sessionTimerRef.current);
        submitAllRef.current?.();
      }
    }, 1000);
    return () => clearInterval(sessionTimerRef.current);
  }, [sessionLimitEnabled, phase]); // eslint-disable-line

  // ── 9. Cleanup ───────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopRecording();
      cancelTTS();
      if (videoRecorderRef.current?.state === "recording") {
        try { videoRecorderRef.current.stop(); } catch {}
      }
      videoRecorderRef._pasteCleanup?.();
      clearTimeout(cheatAlertTimerRef.current);
    };
  }, [stopRecording, cancelTTS]);

  // U3: elapsed timer — resets and ticks every second while recording
  useEffect(() => {
    if (phase !== PHASE.recording) return;
    setRecSecs(0);
    const t = setInterval(() => setRecSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  // U8: question fade — briefly hide then show when displayed question text changes (main Q or follow-up)
  useEffect(() => {
    setQuestionVisible(false);
    const t = setTimeout(() => setQuestionVisible(true), 50);
    return () => clearTimeout(t);
  }, [displayQuestion]);

  // Delivery card auto-advance — 8s countdown then move to next question
  useEffect(() => {
    if (phase !== PHASE.delivery) return;
    setDeliveryCountdown(8);
    let secs = 8;
    const id = setInterval(() => {
      secs -= 1;
      setDeliveryCountdown(secs);
      if (secs <= 0) {
        clearInterval(id);
        deliveryAdvanceRef.current?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Post-AI retake window — 60s countdown then auto-advance
  useEffect(() => {
    if (phase !== PHASE.retake_window) return;
    setRetakeWindowSecs(60);
    let secs = 60;
    const id = setInterval(() => {
      secs -= 1;
      setRetakeWindowSecs(secs);
      if (secs <= 0) {
        clearInterval(id);
        retakeWindowAdvanceRef.current?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Warn before browser refresh/close during active interview or while submitting
  useEffect(() => {
    const safePhases = [PHASE.permissions, PHASE.ready, PHASE.complete, PHASE.error,
                        PHASE.expired, PHASE.already_done, PHASE.camera_required];
    if (safePhases.includes(phase)) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  // ── 10. Render ────────────────────────────────────────────────────────────
  const total = questions.length;

  // ── Static full-screen states (guest mode) ───────────────────────────────
  if (phase === PHASE.expired) return (
    <div className="min-h-screen bg-[#0D1117] flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-lg border border-[#F59E0B]/30 bg-[#161B22] p-10 text-center shadow-lg">
        <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 flex items-center justify-center mx-auto mb-6">
          <span className="text-[#F59E0B] font-bold text-xl">⏱</span>
        </div>
        <div className="font-mono text-[10px] text-[#F59E0B] tracking-[0.2em] mb-2">LINK EXPIRED</div>
        <h2 className="font-mono font-bold text-2xl text-white mb-3">This interview link has expired</h2>
        <p className="text-sm text-white/60 leading-relaxed" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
          {inviteInfo?.role_title && inviteInfo?.company_name
            ? <>Your invitation for <strong className="text-white/80">{inviteInfo.role_title}</strong> at <strong className="text-white/80">{inviteInfo.company_name}</strong> is no longer active.</>
            : "This interview link is no longer active."}
        </p>
        <p className="text-sm text-white/40 mt-3" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>Please contact the hiring team to request a new link.</p>
      </div>
    </div>
  );

  if (phase === PHASE.already_done) return (
    <div className="min-h-screen bg-[#0D1117] flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-lg border border-[#10B981]/30 bg-[#161B22] p-10 text-center shadow-lg">
        <div className="w-12 h-12 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center mx-auto mb-6">
          <svg className="w-6 h-6 text-[#10B981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="font-mono text-[10px] text-[#10B981] tracking-[0.2em] mb-2">COMPLETED</div>
        <h2 className="font-mono font-bold text-2xl text-white mb-2">Interview already submitted</h2>
        <p className="text-sm text-white/60" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
          {inviteInfo?.company_name
            ? <>The hiring team at <strong className="text-white/80">{inviteInfo.company_name}</strong> will be in touch.</>
            : "Your responses have already been submitted. The hiring team will be in touch."}
        </p>
      </div>
    </div>
  );

  if (phase === PHASE.camera_required) return (
    <div className="min-h-screen bg-[#0D1117] flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-lg border border-[#E11D48]/30 bg-[#161B22] p-10 text-center shadow-lg">
        <div className="w-12 h-12 rounded-lg bg-[#E11D48]/10 border border-[#E11D48]/30 flex items-center justify-center mx-auto mb-6">
          <svg className="w-6 h-6 text-[#E11D48]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <div className="font-mono text-[10px] text-[#E11D48] tracking-[0.2em] mb-2">CAMERA REQUIRED</div>
        <h2 className="font-mono font-bold text-2xl text-white mb-3">Camera access is required</h2>
        <p className="text-sm text-white/60 leading-relaxed mb-6" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
          This interview requires camera and microphone access for AI proctoring. You cannot proceed without granting permission.
        </p>
        <div className="rounded-lg border border-[#30363D] bg-[#0D1117] px-5 py-4 text-left mb-6 space-y-2">
          <p className="font-mono text-[10px] text-white/30 tracking-[0.15em] mb-3">HOW TO FIX</p>
          <p className="text-sm text-white/50" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>1. Click the camera / lock icon in your browser's address bar</p>
          <p className="text-sm text-white/50" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>2. Set camera and microphone to <strong className="text-white/70">Allow</strong></p>
          <p className="text-sm text-white/50" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>3. Reload this page and try again</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="w-full rounded-lg bg-[#1F6FEB] hover:bg-[#388BFD] text-white font-mono font-bold text-sm py-3 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  );

  if (phase === PHASE.complete && isGuestMode) return (
    <div className="min-h-screen bg-[#0D1117] flex items-center justify-center p-6">
      <div className="max-w-md w-full space-y-4">
        {/* Thank-you card */}
        <div className="rounded-lg border border-[#10B981]/30 bg-[#161B22] p-10 text-center shadow-lg">
          <div className="w-12 h-12 rounded-full bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center mx-auto mb-6">
            <svg className="w-6 h-6 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="font-mono text-[10px] text-[#10B981] tracking-[0.2em] mb-2">SUBMITTED</div>
          {inviteInfo?.candidate_name ? (
            <>
              <h2 className="font-mono font-bold text-2xl text-white mb-3">
                Thank you, {inviteInfo.candidate_name.split(" ")[0]}.
              </h2>
              <p className="text-sm text-white/60 leading-relaxed" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
                Your interview{inviteInfo.role_title ? ` for ${inviteInfo.role_title}` : ""}
                {inviteInfo.company_name ? ` at ${inviteInfo.company_name}` : ""} has been submitted and evaluated.
              </p>
              <p className="text-sm text-white/40 mt-3" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>The hiring team will review your responses and be in touch soon.</p>
            </>
          ) : (
            <p className="text-sm text-white/60 leading-relaxed" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
              Thank you for completing your interview. Your responses have been submitted successfully.
            </p>
          )}
        </div>

        {/* Session integrity summary */}
        <div className="rounded-lg border border-[#30363D] bg-[#161B22] p-5 shadow-lg">
          <div className="flex items-center gap-2 mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
              className={`w-4 h-4 flex-shrink-0 ${detectedSignalTypes.length === 0 ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
              <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
            </svg>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Session Integrity</span>
          </div>
          {detectedSignalTypes.length === 0 ? (
            <p className="font-mono text-xs text-[#10B981]">✓ Clean session — no integrity events detected</p>
          ) : (
            <div className="space-y-1.5">
              <p className="font-mono text-[10px] text-white/30 mb-2">The following were detected during your session:</p>
              {detectedSignalTypes.map(type => (
                <div key={type} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#F59E0B] flex-shrink-0" />
                  <span className="font-mono text-xs text-white/60">{CHEAT_LABELS[type] || type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (phase === PHASE.ready && isGuestMode) return (
    <div className="min-h-screen bg-[#0D1117] overflow-y-auto">
      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <LogoDark className="h-10 w-auto" />
          <span className="font-mono text-[10px] text-white/30 tracking-[0.2em]">INTERVIEW INVITATION</span>
        </div>

        {inviteInfo ? (
          <>
            <div className="rounded-lg border border-[#30363D] bg-[#161B22] p-8 mb-4 shadow-lg">
              {inviteInfo.company_name && !inviteInfo.company_name.includes("@") && (
                <div className="font-mono text-[10px] text-[#4493F8] tracking-[0.2em] mb-2">{inviteInfo.company_name.toUpperCase()}</div>
              )}
              {inviteInfo.role_title && (
                <h1 className="font-mono font-bold text-3xl text-white mb-1">{inviteInfo.role_title}</h1>
              )}
              {inviteInfo.candidate_name && (
                <p className="text-white/50 text-sm mt-3" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
                  Hi <strong className="text-white/80">{inviteInfo.candidate_name}</strong>, you've been invited to complete an AI-powered recorded video interview.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-[#30363D] bg-[#161B22] p-6 mb-4 space-y-3 shadow-lg">
              {[
                `${total} question${total !== 1 ? "s" : ""} · voice & video · ~${Math.max(1, total) * 4} minutes`,
                "Up to 3 minutes per answer",
                "AI follow-up questions after each answer",
                retakesAllowed === 0 ? "No retakes — first recording is final" : `${retakesAllowed} retake${retakesAllowed !== 1 ? "s" : ""} per question`,
                "Don't close this tab — forfeits remaining retakes",
              ].map((label) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#4493F8] rounded-full flex-shrink-0 mt-1.5" />
                  <span className="font-mono text-xs font-bold text-white/90">{label}</span>
                </div>
              ))}
            </div>

            {/* Environment checklist */}
            <div className="rounded-lg border border-[#30363D] bg-[#161B22] p-6 mb-4 shadow-lg">
              <div className="font-mono text-[10px] text-white/30 tracking-[0.2em] mb-4">BEFORE YOU BEGIN — CHECK THESE</div>
              <div className="space-y-3">
                {[
                  "Face the camera directly — your full face must be visible",
                  "Use a single screen only",
                  "Find a quiet room — no background voices",
                  "No phones, tablets, or notes on your desk",
                  "Ensure good lighting — your face should be clearly lit",
                  "Use headphones if possible",
                  "Close all other browser tabs and apps",
                  "Do not wear sunglasses or cover your face",
                ].map((item, i) => (
                  <label key={i} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={envChecklist[i]}
                      onChange={() => setEnvChecklist(prev => prev.map((v, j) => j === i ? !v : v))}
                      className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#4493F8] cursor-pointer"
                    />
                    <span
                      className={`text-sm leading-relaxed transition-colors ${envChecklist[i] ? "text-white/50 line-through" : "text-white/70 group-hover:text-white/90"}`}
                      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item}
                    </span>
                  </label>
                ))}
              </div>
              {!envChecklist.every(Boolean) && (
                <p className="text-[10px] text-white/20 font-mono mt-4">
                  {envChecklist.filter(Boolean).length}/8 confirmed — tick all to proceed
                </p>
              )}
            </div>

            {/* Combined recording & proctoring notice */}
            <div className="rounded-lg border border-[#F59E0B]/20 bg-[#F59E0B]/5 px-4 py-3 mb-6 flex items-start gap-3 shadow-lg">
              <svg className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div className="space-y-1.5" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
                <p className="text-xs text-white/50 leading-relaxed">
                  <span className="font-bold text-white/70">Session recording.</span>{" "}
                  Your video and audio are recorded for up to <span className="text-white/70 font-semibold">90 days</span> for evaluation purposes only.
                </p>
                <p className="text-xs text-white/50 leading-relaxed">
                  <span className="font-bold text-[#F59E0B]">AI proctoring active.</span>{" "}
                  Face, gaze, audio, tab activity, and clipboard are monitored throughout the interview.
                </p>
              </div>
            </div>

            {/* Consent checkbox */}
            <label className="flex items-start gap-3 cursor-pointer mb-5 group">
              <input
                type="checkbox"
                checked={recordingConsent}
                onChange={(e) => setRecordingConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#4493F8] cursor-pointer"
              />
              <span
                className="text-sm text-white/50 leading-relaxed group-hover:text-white/70 transition-colors"
                style={{ fontFamily: "Inter, system-ui, sans-serif" }}
              >
                I consent to the video and audio recording of this interview session. I understand that the recording may be retained for up to 90 days for evaluation purposes.
              </span>
            </label>

            {total > 0 ? (
              <button
                onClick={handleBegin}
                disabled={!recordingConsent || !envChecklist.every(Boolean)}
                className={`w-full rounded-lg font-mono font-bold text-sm py-4 transition-colors shadow-lg ${
                  recordingConsent && envChecklist.every(Boolean)
                    ? "bg-[#1F6FEB] hover:bg-[#388BFD] text-white cursor-pointer"
                    : "bg-[#1F6FEB]/20 text-white/20 cursor-not-allowed"
                }`}
              >
                Start Interview →
              </button>
            ) : (
              <div className="w-full rounded-lg py-4 flex items-center justify-center gap-2 border border-[#30363D] text-white/30 font-mono text-xs">
                <div className="w-3 h-3 border border-white/20 border-t-white/60 rounded-full animate-spin" />
                Loading interview…
              </div>
            )}
            <p className="text-center text-xs text-white/20 font-mono mt-4">You must accept the recording consent above before starting.</p>
          </>
        ) : (
          <div className="flex items-center justify-center py-24">
            <div className="font-mono text-xs text-white/30 tracking-[0.2em]">Loading interview…</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-[#080808] flex flex-col items-center justify-between overflow-hidden select-none"
      style={{ fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}
    >
      {/* Camera feed — PiP self-view for all modes; video recording only in guest mode */}
      <video
        ref={setVideoRef}
        muted
        autoPlay
        playsInline
        className={
          (phase === PHASE.speaking || phase === PHASE.recording || phase === PHASE.evaluating)
            ? `fixed bottom-6 right-6 z-40 object-cover rounded-sm transition-colors duration-500 border-2 ${faceDetected ? "border-[#10B981]/70" : "border-white/10"}`
            : "hidden"
        }
        style={
          (phase === PHASE.speaking || phase === PHASE.recording || phase === PHASE.evaluating)
            ? { width: 160, height: 120 }
            : undefined
        }
      />

      {/* Silent persistent monitoring indicator — replaces disruptive banner */}
      {phase !== PHASE.ready && phase !== PHASE.permissions && phase !== PHASE.error && phase !== PHASE.complete && phase !== PHASE.processing && (
        <div className="absolute top-4 right-4 z-50 pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`w-4 h-4 transition-colors duration-500 ${
              (cheatCount + browserCheatCount) > 0 ? "text-[#F59E0B]" : "text-white/15"
            }`}
          >
            <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" />
          </svg>
        </div>
      )}
      {/* Session time limit countdown (basic plan) */}
      {sessionLimitEnabled && sessionElapsed >= SESSION_WARN_SECS && phase !== PHASE.complete && phase !== PHASE.processing && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className={`flex items-center gap-1.5 px-3 py-1 font-mono text-[11px] font-bold tracking-wider border ${
            sessionElapsed >= SESSION_LIMIT_SECS - 30
              ? "bg-[#E11D48]/90 border-[#E11D48] text-white"
              : "bg-[#F59E0B]/90 border-[#F59E0B] text-black"
          }`}>
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            {Math.max(0, Math.floor((SESSION_LIMIT_SECS - sessionElapsed) / 60))}:{String(Math.max(0, (SESSION_LIMIT_SECS - sessionElapsed) % 60)).padStart(2, "0")} remaining
          </div>
        </div>
      )}
      {/* Top bar */}
      <div className="w-full flex items-center justify-between px-6 pt-5 pb-2">
        <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">
          {isGuestMode && inviteInfo?.role_title
            ? [inviteInfo.role_title, inviteInfo.company_name].filter(Boolean).join(" · ")
            : phase === PHASE.ready || phase === PHASE.permissions || phase === PHASE.error || phase === PHASE.processing || phase === PHASE.complete || phase === PHASE.evaluating || phase === PHASE.delivery
              ? "Auto Interview"
              : `Question ${qIndex + 1} of ${total}`}
        </div>
        {total > 0 && phase !== PHASE.ready && phase !== PHASE.permissions && phase !== PHASE.error && phase !== PHASE.complete && phase !== PHASE.delivery && phase !== PHASE.retake_window && (
          <div className="flex gap-1.5">
            {Array.from({ length: total }, (_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i < qIndex  ? "bg-[#10B981]" :
                  i === qIndex ? "bg-white"     :
                  "bg-white/15"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 w-full flex flex-col items-center justify-center px-6 gap-8">

        {phase === PHASE.permissions && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            <p className="text-white/40 text-sm tracking-widest uppercase">Requesting permissions…</p>
          </div>
        )}

        {phase === PHASE.error && (
          <div className="max-w-sm text-center flex flex-col items-center gap-6">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E11D48] mb-3">Error</div>
              <p className="text-white/60 text-sm leading-relaxed">{error}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-8 py-3 border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
            >
              Reload page
            </button>
          </div>
        )}

        {phase === PHASE.evaluating && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-[#F59E0B]/20 border-t-[#F59E0B] rounded-full animate-spin" />
            <p className="text-[#F59E0B]/60 text-sm tracking-widest uppercase">Evaluating your answer…</p>
          </div>
        )}

        {phase === PHASE.delivery && deliveryData && (
          <AutoDeliveryCard
            metrics={deliveryData}
            onAdvance={() => deliveryAdvanceRef.current?.()}
            countdown={deliveryCountdown}
          />
        )}

        {phase === PHASE.retake_window && (
          <div className="max-w-xl w-full flex flex-col items-center gap-6 text-center">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#10B981]/30 bg-[#10B981]/5">
              <svg className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-mono text-[10px] text-[#10B981] tracking-[0.1em]">AI REVIEW COMPLETE</span>
            </div>

            <p className="text-white text-base leading-relaxed font-light">{displayQuestion}</p>

            <div className="w-full max-w-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] text-white/40">Re-record window closes in…</span>
                <span className="font-mono text-[10px] font-bold text-[#F59E0B]">{retakeWindowSecs}s</span>
              </div>
              <div className="h-0.5 bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[#F59E0B]/60 transition-all duration-1000"
                  style={{ width: `${(retakeWindowSecs / 60) * 100}%` }}
                />
              </div>
            </div>

            <div className="flex flex-col items-center gap-3 w-full max-w-xs">
              <button
                onClick={handleRetake}
                className="w-full py-3 rounded-xl flex items-center justify-center gap-2 font-mono text-xs font-bold border border-[#F59E0B]/40 text-[#F59E0B] bg-[#F59E0B]/5 hover:bg-[#F59E0B]/10 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Re-record answer
                {(retakesUsedState[qIndex] || 0) > 0 && (
                  <span className="opacity-60 font-normal">· attempt {(retakesUsedState[qIndex] || 0) + 1}</span>
                )}
              </button>
              <button
                onClick={() => retakeWindowAdvanceRef.current?.()}
                className="font-mono text-[11px] text-white/25 hover:text-white/50 transition-colors"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {phase === PHASE.processing && (
          <div className="flex flex-col items-center gap-6 max-w-sm w-full text-center">

            {/* Step tracker removed — evaluation now happens in a single blocking step */}

            {/* Spinner */}
            <div className="w-10 h-10 border-2 border-white/10 border-t-[#0025D8] rounded-full animate-spin" />

            {/* Step title + description */}
            <div>
              {isGuestMode ? (
                <>
                  <p className="text-white font-semibold text-base mb-1">Evaluating your responses</p>
                  <p className="text-white/45 text-xs leading-relaxed">Our AI is reviewing your answers against the role requirements. This usually takes under a minute.</p>
                </>
              ) : (
                <>
                  <p className="text-white font-semibold text-base mb-1">Submitting your answers</p>
                  <p className="text-white/45 text-xs leading-relaxed">Please wait while your responses are being submitted.</p>
                </>
              )}
            </div>

            {/* Do-not-close warning */}
            <div className="flex items-start gap-2.5 rounded-xl border border-[#F59E0B]/25 bg-[#F59E0B]/8 px-4 py-3 w-full text-left">
              <svg className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-[#F59E0B] text-xs leading-relaxed font-mono">
                Do not close or refresh this tab — your submission is in progress. Closing now may result in lost data.
              </p>
            </div>

          </div>
        )}

        {phase === PHASE.complete && (
          <div className="max-w-sm text-center flex flex-col items-center gap-6">
            <div className="w-12 h-12 rounded-full bg-[#10B981]/15 flex items-center justify-center">
              <svg className="w-6 h-6 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#10B981] mb-2">Submitted</div>
              {isGuestMode && inviteInfo?.candidate_name ? (
                <>
                  <h1 className="font-mono font-bold text-2xl text-white mb-2">
                    Thank you, {inviteInfo.candidate_name.split(" ")[0]}.
                  </h1>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Your interview{inviteInfo.role_title ? ` for ${inviteInfo.role_title}` : ""}
                    {inviteInfo.company_name ? ` at ${inviteInfo.company_name}` : ""} has been submitted and evaluated.
                  </p>
                  <p className="text-white/40 text-xs mt-3">The hiring team will review your responses and be in touch soon.</p>
                </>
              ) : (
                <p className="text-white/70 text-sm leading-relaxed">
                  Thank you for completing your interview. Your responses have been submitted successfully.
                </p>
              )}
            </div>
            {/* Background recording upload indicator */}
            {isGuestMode && videoUploadStatus === "uploading" && (
              <div className="flex items-start gap-2.5 rounded-xl border border-[#F59E0B]/25 bg-[#F59E0B]/8 px-4 py-3 w-full text-left">
                <div className="w-3.5 h-3.5 border border-[#F59E0B]/40 border-t-[#F59E0B] rounded-full animate-spin flex-shrink-0 mt-0.5" />
                <p className="text-[#F59E0B] text-xs leading-relaxed font-mono">
                  Please don't close this tab — your session recording is still being saved. This usually takes under a minute.
                </p>
              </div>
            )}
          </div>
        )}

        {phase === PHASE.ready && (
          <div className="max-w-md text-center flex flex-col items-center gap-8">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/30 mb-3">Ready</div>
              <p className="text-white/60 text-sm leading-relaxed">
                {total > 0
                  ? `${total} question${total !== 1 ? "s" : ""} · hands‑free after you begin · speak naturally`
                  : "Loading your interview…"}
              </p>
            </div>
            {total > 0 && (
              <button
                onClick={handleBegin}
                className="px-10 py-3.5 bg-white text-black font-mono text-xs uppercase tracking-[0.25em] hover:bg-white/90 transition-colors"
              >
                Begin Interview
              </button>
            )}
          </div>
        )}

        {(phase === PHASE.speaking || phase === PHASE.recording || phase === PHASE.evaluating) && (
          <>
            <div className="flex items-center justify-center gap-[3px]" style={{ height: 100 }}>
              {barHeights.map((h, i) => (
                <div
                  key={i}
                  style={{ height: Math.max(3, h), width: 4, borderRadius: 2 }}
                  className={`transition-none ${phase === PHASE.speaking ? "bg-[#0025D8]" : "bg-white"}`}
                />
              ))}
            </div>

            <div className="max-w-xl text-center">
              {followUpCount > 0 && (
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F59E0B]/70 mb-2">
                  Follow-up question
                </div>
              )}
              <p
                className="text-white text-base leading-relaxed font-light transition-opacity duration-300"
                style={{ opacity: questionVisible ? 1 : 0 }}
              >
                {displayQuestion}
              </p>
            </div>

            {(phase === PHASE.recording || phase === PHASE.evaluating) && (transcript || interimTranscript) && (
              <div className="max-w-xl w-full">
                <div className="border border-white/10 bg-white/[0.03]">
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/10">
                    <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/30">Transcript</span>
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/15">Read only</span>
                  </div>
                  <div
                    ref={transcriptBoxRef}
                    className="px-4 py-3 max-h-32 overflow-y-auto"
                  >
                    <p className="text-white/60 text-sm leading-relaxed font-mono select-none">
                      {transcript}
                      {interimTranscript && (
                        <span className="text-white/30"> {interimTranscript}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom bar */}
      <div className="w-full flex items-center justify-between px-6 pb-6 pt-2 min-h-[60px]">
        <div className="font-mono text-[11px] tracking-[0.2em] uppercase">
          {phase === PHASE.recording ? (
            <span className="flex items-center gap-2 text-[#E11D48]">
              <span className="inline-block w-2 h-2 rounded-full bg-[#E11D48] animate-pulse" />
              REC {formatTimer(recSecs)}
              {MAX_RECORDING_SECS - recSecs <= 30 && MAX_RECORDING_SECS - recSecs > 0 && (
                <span className="text-[#E11D48] animate-pulse ml-1">
                  · {formatTimer(MAX_RECORDING_SECS - recSecs)} left
                </span>
              )}
            </span>
          ) : phase === PHASE.evaluating ? (
            <span className="text-[#F59E0B]/60">{STATUS_LABEL[PHASE.evaluating]}</span>
          ) : (
            <span className="text-white/30">{STATUS_LABEL[phase]}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {phase === PHASE.retake_window && isGuestMode && (
            <span className="font-mono text-[10px] text-[#F59E0B]/60 tracking-[0.15em]">
              {retakesAllowed > 1
                ? `${retakesAllowed - (retakesUsedState[qIndex] || 0)} retake${retakesAllowed - (retakesUsedState[qIndex] || 0) !== 1 ? "s" : ""} remaining`
                : "1 retake remaining"}
            </span>
          )}
          {!isGuestMode && (phase === PHASE.speaking || phase === PHASE.recording || phase === PHASE.evaluating) && (
            <button
              onClick={() => setExitConfirmOpen(true)}
              className="px-4 py-2 border border-white/10 text-white/25 hover:text-white/50 hover:border-white/20 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
            >
              Exit
            </button>
          )}
        </div>
      </div>

      {exitConfirmOpen && (
        <ExitConfirmOverlay
          anyAnswered={answersRef.current.length > 0 || qIndex > 0}
          onStay={() => setExitConfirmOpen(false)}
          onExit={performExit}
        />
      )}
    </div>
  );
}
